import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { Search01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { cx } from "shared/utils/classnames";
import { useInboxEmbed } from "../context/useInboxEmbed";

const SEARCH_DEBOUNCE_MS = 300;

export type TemplateOption = { id: number; name: string };

export type TemplateSearchResult<T extends TemplateOption> = {
  items: T[];
  isLoading: boolean;
  isError: boolean;
};

export const TemplateSearchList = <T extends TemplateOption>({
  label,
  inputId,
  useTemplates,
  busy,
  onPick,
  onClose
}: {
  label: string;
  inputId: string;
  useTemplates: (search: string) => TemplateSearchResult<T>;
  busy: boolean;
  onPick: (template: T) => void;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [debounced] = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const { items, isLoading, isError } = useTemplates(debounced);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const pick = (template: T | undefined) => {
    if (!template || busy) return;
    onPick(template);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(items[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const renderBody = (): ReactNode => {
    if (isLoading)
      return (
        <div className="px-4 py-3 text-sm text-content-400">Loading...</div>
      );
    if (isError)
      return (
        <div className="px-4 py-3 text-sm text-danger-500">
          Failed to load templates
        </div>
      );
    if (items.length === 0)
      return (
        <div className="px-4 py-3 text-sm text-content-400">
          {debounced
            ? `No templates match "${debounced}"`
            : "No templates available"}
        </div>
      );
    return (
      <div
        ref={listRef}
        role="listbox"
        aria-label={label}
        className="max-h-[246px] overflow-y-auto"
      >
        {items.map((template, index) => (
          <div key={template.id}>
            {index > 0 ? <div className="h-px w-full bg-alpha-40" /> : null}
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              data-index={index}
              title={template.name}
              disabled={busy}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => pick(template)}
              className={cx(
                "block w-full truncate px-4 py-2.5 text-left text-sm font-medium text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-50",
                index === activeIndex ? "bg-alpha-40" : ""
              )}
            >
              {template.name}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-alpha-100 bg-white shadow-[0_8px_8px_-4px_rgba(28,29,34,0.03),0_20px_24px_-4px_rgba(28,29,34,0.08)]">
      <div className="border-b border-alpha-40 p-3">
        <BaseInput
          id={inputId}
          size={InputSize.SM}
          Icon={Search01Icon}
          placeholder="Search templates"
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoFocus
        />
      </div>
      {renderBody()}
    </div>
  );
};

export const PickerShell = ({
  hidden,
  disabled,
  open,
  onOpenChange,
  children
}: {
  hidden: boolean;
  disabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) => {
  const embed = useInboxEmbed();
  if (hidden) return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="top-end">
      <PopoverTrigger
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (disabled) return;
          onOpenChange(!open);
        }}
      >
        <Button
          variant={ButtonVariant.BASE}
          size={embed?.compact ? ButtonSize.XS : ButtonSize.SM}
          disabled={disabled}
          className="!text-sm !font-bold shadow-xs"
        >
          Templates
        </Button>
      </PopoverTrigger>
      <PopoverContent className={embed?.menuZClass ?? "z-[12]"}>
        {children}
      </PopoverContent>
    </Popover>
  );
};
